import { auth } from '../services/firebase/firebaseConfig'

/**
 * Who is making this write — for `fields.User`, which is the ONLY thing the
 * order-history row has to go on (functions createBillingHistory reads
 * `UserEmail` straight off the document; a Firestore trigger gets no auth
 * context of its own).
 *
 * Redux alone is not enough. `state.user.email` is null until initUserData runs
 * (mainRoutes, after the auth-validation round trip) and goes back to null on
 * every resetUserData — sign-out, ACCOUNT_INACTIVE, a failed re-validation — any
 * of which can land while a form is still open. Firebase's own session object
 * survives all of those until the user is actually signed out, so it is the
 * better source; redux stays first only because it is already loaded.
 *
 * Returns '' when nobody can be resolved. Callers MUST treat that as "don't
 * write the field" rather than writing the empty value: `merge: true` means
 * omitting the key preserves whatever is there, while writing '' destroys a good
 * value permanently and makes every later history row read "Undefined".
 */
export function resolveActorEmail(preferred?: string | null, state?: any): string {
  // Never let resolving the actor be the thing that fails a save. Every hop is
  // optional-chained, and the Firebase read is wrapped: `auth` is a module-level
  // getAuth() whose `currentUser` access can throw if the SDK failed to
  // initialise, and optional chaining does not catch a throw. Tests also mock
  // firebaseConfig down to the exports they use, leaving `auth` undefined.
  let sessionEmail: string | null | undefined
  try {
    sessionEmail = auth?.currentUser?.email
  } catch {
    sessionEmail = undefined
  }

  return (
    preferred ||
    state?.user?.email ||
    sessionEmail ||
    state?.user?.id ||
    ''
  )
}

/**
 * Add the actor to a dot-notation Firestore patch, or leave the patch untouched
 * when there is no actor to write. See the note above on why '' is not written.
 */
export function withActor<T extends Record<string, any>>(
  patch: T,
  actor?: string | null,
): T {
  if (!patch || !actor) return patch
  return { ...patch, 'fields.User': actor }
}
